import * as React from 'react';

import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { TextField, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import AddIcon from '@mui/icons-material/Add';

import { gridSpacing } from 'store/constant';
import { getCampaigns, addInfluencerToCampaign } from 'api_handlers/campaign';
import { getPlans, addPlanProfiles, createPlan } from 'api_handlers/planner2';

import CampaignCard from './CampaignCard';
import mixpanel from '../../../../utils/mixpanel';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const GOLD_BTN = {
  textTransform: 'none',
  fontWeight: 700,
  px: 3,
  background: '#febd1c',
  color: '#111936',
  boxShadow: '0 6px 14px -6px rgba(254,189,28,0.8)',
  '&:hover': { background: '#f0b016' },
};

export default function AdvancedFilters({ open, setOpen, influencers, text, v2 }) {
  const isList = text === 'Planner';
  const [campaigns, setCampaigns] = React.useState([]);
  const [addedInCampaigns, setAddedInCampaigns] = React.useState([]);
  const [planner, setPlanner] = React.useState([]);
  const [addedInPlan, setAddedInPlan] = React.useState([]);
  const [isCreatingPlan, setIsCreatingPlan] = React.useState(false);
  const [planName, setPlanName] = React.useState('');
  const [tags, setTags] = React.useState('');
  const [platform, setPlatform] = React.useState('all');
  const [errors, setErrors] = React.useState({ planName: 'List Name is required' });
  const user = useSelector((state) => state.planner2.user);

  const handleClose = () => {
    setAddedInPlan([]);
    setOpen(false);
    setIsCreatingPlan(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = async () => {
    try {
      if (text === 'Planner') {
        // Fetch data for Planner
        const plannerData = await getPlans(user.brand.id);
        console.log('Planner Data', plannerData.results.plans_lists);
        const plansArray = Object.keys(plannerData.results.plans_lists || {}).map((key) => ({
          ...plannerData.results.plans_lists[key],
          planName: key,
        }));
        console.log('planner', planner);
        setPlanner(plansArray);
      } else {
        // Fetch default campaigns data
        const resp = await getCampaigns();
        setCampaigns(resp);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  React.useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleAddToCampaign = async (campaignId) => {
    const { data } = await addInfluencerToCampaign(campaignId, {
      profiles: influencers.map((influencer) => influencer.id),
    });

    if (data.success) {
      setAddedInCampaigns((prevValue) => [...prevValue, campaignId]);
    }
  };

  const handlePlanner = async (planId) => {
    try {
      const { data } = await addPlanProfiles(planId, {
        iq_profile_ids: influencers.map((influencer) => influencer.id),
      });

      if (data.msg) {
        setAddedInPlan((prevValue) => [...prevValue, planId]);
        console.log(data.msg);
      }
    } catch (error) {
      console.error('Error adding influencers to the plan:', error);
    }
  };

  const handleCreatePlan = () => {
    if (process.env.NODE_ENV === 'production') {
      mixpanel.track("discover_create_list", {
        "type": "collab_list"
      })
    }
    setIsCreatingPlan(true);
  };

  const handleSubmitPlan = async () => {
    // Validate Plan Name
    if (!planName.trim()) {
      setErrors({ planName: 'List Name is required' });
      return;
    }

    // Proceed with creating the plan
    try {
      const response = await createPlan({
        name: planName,
        brand_id: user.brand.id, // Replace with actual brand_id
        tags: tags,
        platform: platform,
        iq_profile_ids: influencers.map((influencer) => influencer.id),
      });
      if (process.env.NODE_ENV === 'production') {
        mixpanel.track("collab_list_created", {
          name: planName,
          brand_id: user.brand.id,
          tags: tags,
          platform: platform,
          source: 'discover'
        })
    }
      setAddedInPlan([...addedInPlan, response.data.plan_id])
      console.log('Create Response: ', response);

      if (response.status === 201) {
        setPlanName('');
        setTags('');
        setPlatform('all');
        setErrors({});
        setIsCreatingPlan(false); // Close the form
        fetchData();
      } else {
        // console.error('Error creating plan:', data);
      }
    } catch (error) {
      console.error('Error submitting plan:', error);
    }
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        PaperProps={v2 ? { className: 'dv2-cmp-paper' } : undefined}
      >
        <DialogTitle
          sx={{ m: 0, p: v2 ? '14px 20px' : 2, display: 'flex', alignItems: 'center', gap: 1.25 }}
          id="customized-dialog-title"
        >
          {v2 && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '9px',
                background: 'linear-gradient(135deg,#111936,#26315f)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isList ? (
                <FormatListBulletedRoundedIcon sx={{ color: '#febd1c', fontSize: 18 }} />
              ) : (
                <CampaignOutlinedIcon sx={{ color: '#febd1c', fontSize: 19 }} />
              )}
            </Box>
          )}
          <span style={v2 ? { fontWeight: 800, fontSize: 18, color: '#111936' } : {}}>
            {text === 'AddToCampaign' ? 'Add To Campaign' : text === 'Planner' ? 'Collab List' : ''}
          </span>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            // color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          {!isCreatingPlan ? (
            <Grid container direction="row" spacing={gridSpacing}>
              {text === 'AddToCampaign'
                ? campaigns.map((campaign, index) => (
                    <Grid key={index} item xs={12} sm={6} md={6} lg={6}>
                      <CampaignCard
                        {...campaign}
                        v2={v2}
                        isList={false}
                        added={addedInCampaigns.includes(campaign.id)}
                        handleAddToCampaign={handleAddToCampaign}
                      />
                    </Grid>
                  ))
                : text === 'Planner'
                ? planner.map((plan, index) => (
                    <Grid key={plan.id} item xs={12} sm={6} md={6} lg={6}>
                      <CampaignCard
                        id={plan.id}
                        v2={v2}
                        isList={true}
                        platform={plan.platform}
                        total_influencers={plan.total_influencers}
                        tags={plan.tags}
                        created_at={plan.created_at}
                        // created_by={user.brand.}
                        updated_at={plan.updated_at}
                        planName={plan.planName}
                        added={addedInPlan.includes(plan.id)}
                        handleAddToCampaign={handlePlanner}
                      />
                    </Grid>
                  ))
                : ''}
            </Grid>
          ) : text === 'Planner' ? (
            <form noValidate>
              <TextField
                label="List Name"
                fullWidth
                required
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                error={Boolean(errors.planName)}
                helperText={errors.planName}
                margin="normal"
              />
              <TextField
                label="Platform"
                fullWidth
                select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                margin="normal"
              >
                <MenuItem value="instagram">Instagram</MenuItem>
                <MenuItem value="youtube">YouTube</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </TextField>
              <TextField
                label="Tags"
                fullWidth
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                margin="normal"
              />
            </form>
          ) : (
            setIsCreatingPlan(false)
          )}
        </DialogContent>
        <DialogActions>
          {!isCreatingPlan ? (
            text === 'Planner' ? (
              <Grid container justifyContent={'end'} spacing={2}>
                <Grid item>
                  <Button
                    variant={v2 ? 'text' : 'contained'}
                    color="primary"
                    onClick={handleClose}
                    sx={v2 ? { textTransform: 'none', fontWeight: 600, color: '#5b6478' } : undefined}
                  >
                    Done
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreatePlan} // Handle the create plan logic here
                    startIcon={v2 ? <AddIcon /> : undefined}
                    sx={v2 ? GOLD_BTN : undefined}
                  >
                    Create List
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Button
                autoFocus
                variant={v2 ? 'contained' : 'text'}
                onClick={() => {
                  handleClose();
                }}
                sx={v2 ? GOLD_BTN : undefined}
              >
                Close
              </Button>
            )
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitPlan} // Handle plan submission
              sx={v2 ? GOLD_BTN : undefined}
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </BootstrapDialog>
    </React.Fragment>
  );
}
